import { Controller } from '@hotwired/stimulus'

// Lock broadcasts die with a backgrounded tab: Safari suspends the page and
// its cable connection, and missed Turbo Stream messages are never replayed.
// When the tab wakes, refetch the page and swap any lock wrapper whose state
// changed — untouched ones are left alone so a half-typed answer survives.
export default class extends Controller {
  static values = { minHiddenSeconds: { type: Number, default: 5 } }

  connect() {
    this.refresh = this.refresh.bind(this)
    this.keepForm = this.keepForm.bind(this)
    document.addEventListener('visibilitychange', this.refresh)
    document.addEventListener('turbo:before-stream-render', this.keepForm)
  }

  disconnect() {
    document.removeEventListener('visibilitychange', this.refresh)
    document.removeEventListener('turbo:before-stream-render', this.keepForm)
  }

  markTouched(event) {
    const wrapper = event.target.closest('div[id^="question_"][id$="_lock"]')
    if (wrapper) wrapper.dataset.touched = ''
  }

  keepForm(event) {
    const stream = event.target
    const wrapper = document.getElementById(stream.target)
    if (!wrapper || !this.element.contains(wrapper) || !this.hasTouchedForm(wrapper)) return

    const unlocking = Boolean(stream.templateContent.querySelector('form'))
    event.detail.render = () => (unlocking ? this.unlock(wrapper) : this.lock(wrapper, stream.templateContent))
  }

  lock(wrapper, placeholder) {
    this.unlock(wrapper)
    wrapper.classList.add('relative')
    wrapper.firstElementChild.inert = true

    const cover = document.createElement('div')
    cover.dataset.lockCover = ''
    cover.className = 'absolute inset-0 z-10 overflow-hidden rounded-3xl bg-surface'
    cover.append(placeholder)
    cover.firstElementChild.classList.add('h-full')
    wrapper.append(cover)
  }

  unlock(wrapper) {
    wrapper.querySelector(':scope > [data-lock-cover]')?.remove()
    wrapper.firstElementChild.inert = false
  }

  async refresh() {
    if (document.visibilityState !== 'visible') {
      this.hiddenAt = Date.now()
      return
    }

    // A quick alt-tab never suspends the cable; only a real absence warrants
    // refetching the page.
    if (this.hiddenAt && Date.now() - this.hiddenAt < this.minHiddenSecondsValue * 1000) return

    const response = await fetch(location.href, { headers: { Accept: 'text/html' } })
    if (!response.ok) return

    const fresh = new DOMParser().parseFromString(await response.text(), 'text/html')

    this.element.querySelectorAll('div[id$="_lock"]').forEach((current) => {
      const next = fresh.getElementById(current.id)
      if (!next || this.state(next) === this.state(current)) return

      if (this.hasTouchedForm(current)) {
        this.state(next) === 'form' ? this.unlock(current) : this.lock(current, this.fragmentOf(next))
      } else {
        current.replaceChildren(...next.children)
      }
    })
  }

  hasTouchedForm(wrapper) {
    return 'touched' in wrapper.dataset && Boolean(wrapper.querySelector('form'))
  }

  fragmentOf(wrapper) {
    const fragment = document.createDocumentFragment()
    fragment.append(...wrapper.children)
    return fragment
  }

  // A question wrapper holds a form when unlocked; a spark wrapper holds the
  // overlay when locked and nothing when open.
  state(wrapper) {
    if (wrapper.querySelector(':scope > [data-lock-cover]')) return 'locked'
    if (wrapper.querySelector('form')) return 'form'

    return wrapper.children.length ? 'locked' : 'open'
  }
}
